/**
 * VersionContext
 * React context for version checking and update notifications
 * 
 * Provides:
 * - updateAvailable: boolean indicating if update is available
 * - currentVersion: current app version
 * - refresh: function to trigger page reload
 * - dismissUpdate: function to dismiss update banner
 * - checkNow: function to manually check for updates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import VersionCheckOrchestrator from '../services/versionCheck/VersionCheckOrchestrator';

// Create context
const VersionContext = createContext(null);

/**
 * VersionProvider component
 * Wraps the app and provides version checking functionality
 */
export function VersionProvider({ children, options = {} }) {
  const [state, setState] = useState({
    initialized: false,
    updateAvailable: false,
    currentVersion: null,
    newVersion: null,
    isChecking: false,
    lastCheckTime: null,
    error: null
  });

  const [orchestrator] = useState(() => new VersionCheckOrchestrator({
    pollInterval: options.pollInterval || 300000, // 5 minutes
    mobilePollInterval: options.mobilePollInterval || 600000, // 10 minutes
    immediateCheck: options.immediateCheck !== false,
    enableVisibilityHandling: options.enableVisibilityHandling !== false,
    debug: options.debug || false,
    onUpdateAvailable: (bannerState) => {
      setState(prev => ({
        ...prev,
        updateAvailable: true,
        newVersion: bannerState.newVersion
      }));
    },
    onError: (error) => {
      setState(prev => ({
        ...prev,
        error: error.message
      }));
      if (options.onError) {
        options.onError(error);
      }
    }
  }));

  // Initialize orchestrator on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const result = await orchestrator.initialize();
      
      if (mounted) {
        setState(prev => ({
          ...prev,
          initialized: result.success,
          currentVersion: result.initialVersion || null,
          error: result.error || null
        }));
      }
    };

    init();

    // Subscribe to banner state changes
    const unsubscribe = orchestrator.subscribe((bannerState) => {
      if (mounted) {
        setState(prev => ({
          ...prev,
          updateAvailable: bannerState.visible,
          newVersion: bannerState.newVersion || null
        }));
      }
    });

    // Cleanup on unmount
    return () => {
      mounted = false;
      unsubscribe();
      orchestrator.destroy();
    };
  }, [orchestrator]);

  /**
   * Trigger a page reload to get the new version
   */
  const refresh = useCallback((hard = true) => {
    orchestrator.triggerReload(hard);
  }, [orchestrator]);

  /**
   * Dismiss the update notification
   */
  const dismissUpdate = useCallback(() => {
    orchestrator.dismissBanner();
    setState(prev => ({
      ...prev,
      updateAvailable: false
    }));
  }, [orchestrator]);

  /**
   * Manually check for updates
   */
  const checkNow = useCallback(async () => {
    setState(prev => ({ ...prev, isChecking: true }));
    
    const result = await orchestrator.checkNow();
    
    setState(prev => ({
      ...prev,
      isChecking: false,
      lastCheckTime: Date.now(),
      updateAvailable: result.updateAvailable || prev.updateAvailable,
      error: result.error || null
    }));

    return result;
  }, [orchestrator]);

  /**
   * Get health check status
   */
  const getHealthStatus = useCallback(() => {
    return orchestrator.runHealthCheck();
  }, [orchestrator]);

  /**
   * Get full orchestrator state
   */
  const getOrchestratorState = useCallback(() => {
    return orchestrator.getState();
  }, [orchestrator]);

  const value = {
    // State
    ...state,
    
    // Actions
    refresh,
    dismissUpdate,
    checkNow,
    
    // Debug/Health
    getHealthStatus,
    getOrchestratorState
  };

  return (
    <VersionContext.Provider value={value}>
      {children}
    </VersionContext.Provider>
  );
}

/**
 * Hook to access version context
 */
export function useVersion() {
  const context = useContext(VersionContext);
  
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  
  return context;
}

/**
 * Hook for just the update available state
 */
export function useUpdateAvailable() {
  const { updateAvailable, newVersion, refresh, dismissUpdate } = useVersion();
  
  return {
    updateAvailable,
    newVersion,
    refresh,
    dismissUpdate
  };
}

export default VersionContext;
